import { useEffect, useRef } from 'react';

// Everything the browser would normally stop at on Tab. Disabled controls and
// explicit tabindex="-1" are excluded; hidden ones are filtered at use time,
// since visibility changes with the step the dialog is showing.
const TABBABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * Focus management for a `role="dialog" aria-modal="true"` container: moves
 * focus into the dialog when it opens, keeps Tab cycling inside it while focus
 * is inside it, and restores focus to whatever was focused before on close.
 * Returns the ref to attach to the dialog element, which must also carry
 * `tabIndex={-1}` so it can hold focus itself when it has no focusable content
 * yet.
 *
 * Cycling deliberately only applies while focus is already inside the dialog:
 * overlays that open on top of it render outside this subtree (the boost modal
 * and the photo lightbox portal into `document.body`, the signup/login modal is
 * rendered by AppContent), and pulling focus out of those and back behind them
 * left their buttons unusable.
 *
 * `isOpen` must only be true once the dialog element is actually rendered — the
 * flows keep it mounted through their close animation, so pass their
 * "mounted AND open" state, not the raw prop.
 */
export function useDialogFocus<T extends HTMLElement>(isOpen: boolean) {
  const ref = useRef<T>(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.activeElement as HTMLElement | null;
    const dialog = ref.current;
    // A step may already have auto-focused its own input (child effects run
    // first); pulling focus back to the container would undo that.
    if (dialog && !dialog.contains(document.activeElement)) dialog.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || !ref.current) return;
      const active = document.activeElement;
      // Whoever holds focus owns the tab order. If that is not this dialog, an
      // overlay outside its subtree (or the page behind it) is in charge, so
      // leave the browser's own Tab handling alone.
      if (!ref.current.contains(active)) return;
      const items = Array.from(
        ref.current.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR),
      ).filter((el) => el.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        ref.current.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previous?.focus();
    };
  }, [isOpen]);

  return ref;
}

export default useDialogFocus;
